import React, { useState, useEffect } from "react";
import {
  Table, Card, Row, Col, Space, Typography, Layout, Statistic, Button,
  DatePicker, Tag, Tooltip
} from "antd";
import {
  FaPhoneAlt, FaExclamationTriangle, FaFileExcel, FaCalendarAlt,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import api from "../api/Api";

const { Title, Text } = Typography;
const { Content } = Layout;
const { RangePicker } = DatePicker;

const DATE_FORMAT = "YYYY-MM-DD";

// ✅ Normaliza string para comparação segura
const norm = (str) => (str || "").trim().toLowerCase();

const RelatorioChamadas = () => {
  const [datas, setDatas] = useState([dayjs(), dayjs()]);
  const [loading, setLoading] = useState(false);
  const [chamadas, setChamadas] = useState([]);
  const [estatisticas, setEstatisticas] = useState({ IPU: 0, engano: 0, total: 0 });

  useEffect(() => {
    if (datas && datas[0] && datas[1]) fetchRelatorio();
  }, [datas]);

  const fetchRelatorio = async () => {
    try {
      setLoading(true);
      const dataInicio = datas[0].format(DATE_FORMAT);
      const dataFim    = datas[1].format(DATE_FORMAT);

      const response = await api.ListChamadaRelatorio2({ dataInicio, dataFim });
      const lista = Array.isArray(response) ? response : [];

      console.log(response);

      setChamadas(lista);

      // ✅ Usa norm() para comparar sem problemas de case/espaços
      setEstatisticas({
        IPU:    lista.filter(item => norm(item.tipo) === "ipu").length,
        engano: lista.filter(item => norm(item.tipo) === "engano").length,
        total:  lista.length,
      });
    } catch (error) {
      toast.error("Erro ao carregar relatório de chamadas");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    try {
      const dadosExport = chamadas.map((item, index) => ({
        Nº:           index + 1,
        Data:         item.data ? dayjs(item.data).format("DD/MM/YYYY HH:mm:ss") : "",
        Tipo:         item.tipo || "",
        Utente:       item.utente || "",
        Província:    item.provincia || "",
        Pergunta:     item.pergunta || "",
        Observações:  item.obs || "",
        Operador:     item.operador || "",
        
      }));

      const worksheet  = XLSX.utils.json_to_sheet(dadosExport);
      const workbook   = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Chamadas");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob        = new Blob([excelBuffer], { type: "application/octet-stream" });
      const fileName    = `relatorio_chamadas_${datas[0].format("YYYYMMDD")}_${datas[1].format("YYYYMMDD")}.xlsx`;

      saveAs(blob, fileName);
      toast.success("Relatório exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar para Excel");
      console.error(error);
    }
  };

  const columns = [
    {
      title: "Data/Hora",
      dataIndex: "data",
      key: "data",
      render: (text) => text ? dayjs(text).format("DD/MM/YYYY HH:mm:ss") : "-",
      sorter: (a, b) => dayjs(a.data).unix() - dayjs(b.data).unix(),
      defaultSortOrder: "descend",
      width: 160,
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      // ✅ Usa norm() no render e no onFilter
      render: (tipo) => {
        const t = norm(tipo);
        return (
          <Tag
            color={t === "ipu" ? "green" : t === "engano" ? "orange" : "default"}
            icon={t === "ipu" ? <FaPhoneAlt /> : <FaExclamationTriangle />}
          >
            {tipo || "—"}
          </Tag>
        );
      },
      filters: [
        { text: "IPU",    value: "ipu"    },
        { text: "Engano", value: "engano" },
      ],
      onFilter: (value, record) => norm(record.tipo) === value,
      width: 100,
    },
    {
      title: "Utente",
      dataIndex: "utente",
      key: "utente",
      render: (text) => text || <Text type="secondary">—</Text>,
      ellipsis: true,
    },
    {
      title: "Província",
      dataIndex: "provincia",
      key: "provincia",
      render: (text) => text || <Text type="secondary">—</Text>,
      width: 120,
    },
    {
      title: "Pergunta",
      dataIndex: "pergunta",
      key: "pergunta",
      render: (text) =>
        text ? (
          <Tooltip title={text}>
            <span style={{ maxWidth: 200, display: "inline-block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {text}
            </span>
          </Tooltip>
        ) : <Text type="secondary">—</Text>,
    },
    {
      title: "Observações",
      dataIndex: "obs",
      key: "obs",
      render: (text) =>
        text ? (
          <Tooltip title={text}>
            <span style={{ maxWidth: 150, display: "inline-block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {text}
            </span>
          </Tooltip>
        ) : <Text type="secondary">—</Text>,
    },
    {
      title: "Operador",
      dataIndex: "operador",
      key: "operador",
      render: (text) => text || <Text type="secondary">—</Text>,
    },
    
  ];

  return (
    <Layout>
      <Content style={{ padding: 24 }}>
        <ToastContainer />

        <Card
          title={
            <Space>
              <FaPhoneAlt style={{ fontSize: 20, color: "#1677ff" }} />
              <Title level={4} style={{ margin: 0 }}>Relatório de Chamadas</Title>
            </Space>
          }
          extra={
            <Space>
              <RangePicker
                value={datas}
                onChange={setDatas}
                format="DD/MM/YYYY"
                allowClear={false}
                style={{ width: 260 }}
              />
              <Button
                type="primary"
                icon={<FaFileExcel />}
                onClick={exportToExcel}
                disabled={chamadas.length === 0}
              >
                Exportar Excel
              </Button>
            </Space>
          }
        >
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card size="small" hoverable style={{ borderLeft: "4px solid #52c41a", borderRadius: 10 }}>
                <Statistic
                  title="Total IPU"
                  value={estatisticas.IPU}
                  valueStyle={{ color: "#52c41a", fontSize: 28 }}
                  prefix={<FaPhoneAlt />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" hoverable style={{ borderLeft: "4px solid #fa8c16", borderRadius: 10 }}>
                <Statistic
                  title="Total Engano"
                  value={estatisticas.engano}
                  valueStyle={{ color: "#fa8c16", fontSize: 28 }}
                  prefix={<FaExclamationTriangle />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" hoverable style={{ borderLeft: "4px solid #1677ff", borderRadius: 10 }}>
                <Statistic
                  title="Total Geral"
                  value={estatisticas.total}
                  valueStyle={{ color: "#1677ff", fontSize: 28 }}
                  prefix={<FaCalendarAlt />}
                />
              </Card>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={chamadas}
            rowKey={(_, index) => index}
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: 1200 }}
            locale={{ emptyText: "Nenhuma chamada encontrada no período" }}
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default RelatorioChamadas;